import React from "react";
import PropTypes from "prop-types";
import Close from "@material-ui/icons/Close";

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const isValidEmail = (value) => EMAIL_REGEX.test(String(value || "").trim());

const defaultNormalize = (value) => {
    const email = String(value || "").trim().toLowerCase();
    return isValidEmail(email) ? email : "";
};

const defaultDescribeError = (rejected) => "Not a valid email address: "+rejected.join(", ");

/**
 * Tokenized input for email addresses (default) or other values such as domains.
 * Adds on Enter, Tab, comma, semicolon, space, new line or blur; pasted lists are split.
 * Invalid entries stay in the text box with an inline error; duplicates are merged
 * and reported inline.
 *
 * normalize(value) returns the clean value or "" when it is invalid.
 * describeError(rejected[]) returns the message for invalid entries.
 */
function FarEmailChips({ id, emails, onChange, classes, placeholder, invalid, describedBy, label, normalize, describeError, max, chipClassName, itemsLabel }){
    const [text, setText] = React.useState("");
    const [error, setError] = React.useState("");
    const [notice, setNotice] = React.useState("");

    const commit = (raw) => {
        const parts = String(raw || "").split(/[\s,;]+/).map(p => p.trim()).filter(p => p.length > 0);
        if(parts.length <= 0){
            setText("");
            return true;
        }
        const next = emails.slice();
        const rejected = [];
        const duplicates = [];
        let overLimit = false;
        parts.forEach(part => {
            const value = normalize(part);
            if(!value){
                rejected.push(part);
            }else if(next.includes(value)){
                if(!duplicates.includes(value)){ duplicates.push(value); }
            }else if(max && next.length >= max){
                overLimit = true;
                rejected.push(part);
            }else{
                next.push(value);
            }
        });
        if(next.length !== emails.length){
            onChange(next);
        }
        setText(rejected.join(", "));
        if(overLimit){
            setError("You can add at most "+max+" "+itemsLabel+".");
        }else{
            setError(rejected.length > 0 ? describeError(rejected) : "");
        }
        setNotice(duplicates.length > 0 ? "Already added: "+duplicates.join(", ")+"." : "");
        return rejected.length <= 0;
    };

    const onKeyDown = (e) => {
        if(e.key === "Enter" || e.key === "," || e.key === ";" || (e.key === "Tab" && text.trim().length > 0)){
            e.preventDefault();
            commit(text);
        }else if(e.key === "Backspace" && text.length <= 0 && emails.length > 0){
            onChange(emails.slice(0, emails.length - 1));
        }
    };

    const remove = (email) => {
        onChange(emails.filter(item => item !== email));
        setNotice("");
    };

    const errorId = id+"-error";
    const noticeId = id+"-notice";
    return (
        <div>
            <div className={classes.chips+" "+(invalid || error ? classes.inputError : "")}>
                {
                    emails.map(email => (
                        <span className={classes.chip+(chipClassName ? " "+chipClassName : "")} key={email}>
                            {email}
                            <button type="button" className={classes.chipRemove} aria-label={"Remove "+email} onClick={() => remove(email)}>
                                <Close />
                            </button>
                        </span>
                    ))
                }
                <input
                    id={id}
                    className={classes.chipInput}
                    type="text"
                    value={text}
                    placeholder={emails.length <= 0 ? placeholder : "Add another…"}
                    aria-label={label}
                    aria-invalid={invalid || error ? "true" : "false"}
                    aria-describedby={[describedBy, error ? errorId : null, notice ? noticeId : null].filter(Boolean).join(" ") || undefined}
                    onChange={(e) => { setText(e.target.value); if(error){ setError(""); } if(notice){ setNotice(""); } }}
                    onKeyDown={onKeyDown}
                    onBlur={() => commit(text)}
                    onPaste={(e) => {
                        const pasted = e.clipboardData ? e.clipboardData.getData("text") : "";
                        if(/[\s,;]/.test(pasted)){
                            e.preventDefault();
                            commit(text+" "+pasted);
                        }
                    }}
                />
            </div>
            { error ? <p className={classes.error} id={errorId} role="alert">{error}</p> : null }
            { notice ? <p className={classes.help} id={noticeId} role="status">{notice}</p> : null }
        </div>
    );
}

FarEmailChips.defaultProps = {
    placeholder: "name@company.com",
    invalid: false,
    describedBy: null,
    label: "Email addresses",
    normalize: defaultNormalize,
    describeError: defaultDescribeError,
    max: 0,
    chipClassName: "",
    itemsLabel: "entries"
};

FarEmailChips.propTypes = {
    id: PropTypes.string.isRequired,
    emails: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    classes: PropTypes.object.isRequired,
    placeholder: PropTypes.string,
    invalid: PropTypes.bool,
    describedBy: PropTypes.string,
    label: PropTypes.string,
    normalize: PropTypes.func,
    describeError: PropTypes.func,
    max: PropTypes.number,
    chipClassName: PropTypes.string,
    itemsLabel: PropTypes.string
};

export default FarEmailChips;
